import React, { useState } from 'react';
import Color from 'color';
import { ButtonBase, Collapse, Dialog, Grid, useTheme } from '@mui/material';
import { CheckCircle, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  Currency,
  ProjectSelectionCount,
  ProjectSelectionLineInput,
  PublicCatalogCategoryData,
  PublicCatalogItemData,
} from '../api';
import { OrderPageTypography } from '../orderPage/themedComponents/OrderPageTypography';
import NumberSelector from '../orderPage/ui-objects/NumberSelector';
import { priceFormatterFromCurrency } from '../orderPage/utils';
import { useLanguage } from '../utils/GeneralUtils';
import { getSelectedProjectQuantity } from './projectSelectionUtils';

const COLLAPSIBLE_FROM_TOTAL_ITEMS = 10;

type ProjectSelectionPickerProps = {
  categories: PublicCatalogCategoryData[];
  selectionCount: ProjectSelectionCount;
  requiredQuantity: number;
  currency: Currency;
  selection: ProjectSelectionLineInput[];
  onChange: (selection: ProjectSelectionLineInput[]) => void;
};

const initiallyExpandedCategoryIds = (
  categories: PublicCatalogCategoryData[],
  selection: ProjectSelectionLineInput[],
): Set<string> => {
  const selectedItemIds = new Set(selection.map((line) => line.catalog_item_id));
  const categoriesWithSelection = categories.filter((category) =>
    (category.items || []).some((item) => selectedItemIds.has(item.id)),
  );
  if (categoriesWithSelection.length > 0) {
    return new Set(categoriesWithSelection.map((category) => category.id));
  }
  return new Set(categories.length ? [categories[0].id] : []);
};

export const ProjectSelectionPicker = ({
  categories,
  selectionCount,
  requiredQuantity,
  currency,
  selection,
  onChange,
}: ProjectSelectionPickerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const language = useLanguage();
  const isSingle = selectionCount === ProjectSelectionCount.SINGLE;
  const selectedQuantity = getSelectedProjectQuantity(selection);
  const totalItems = categories.reduce((total, category) => total + (category.items || []).length, 0);
  const isCollapsible = totalItems >= COLLAPSIBLE_FROM_TOTAL_ITEMS;
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(() =>
    initiallyExpandedCategoryIds(categories, selection),
  );
  const [enlargedItem, setEnlargedItem] = useState<PublicCatalogItemData | null>(null);

  const toggleCategory = (categoryId: string) => {
    const updatedIds = new Set(expandedCategoryIds);
    if (updatedIds.has(categoryId)) {
      updatedIds.delete(categoryId);
    } else {
      updatedIds.add(categoryId);
    }
    setExpandedCategoryIds(updatedIds);
  };

  const quantityOf = (catalogItemId: string) =>
    selection.find((line) => line.catalog_item_id === catalogItemId)?.quantity || 0;

  const setItemQuantity = (catalogItemId: string, quantity: number) => {
    const otherLines = selection.filter((line) => line.catalog_item_id !== catalogItemId);
    onChange(quantity > 0 ? [...otherLines, { catalog_item_id: catalogItemId, quantity }] : otherLines);
  };

  const toggleSingleSelection = (catalogItemId: string) => {
    onChange(quantityOf(catalogItemId) > 0 ? [] : [{ catalog_item_id: catalogItemId, quantity: 1 }]);
  };

  const selectedQuantityInCategory = (category: PublicCatalogCategoryData) =>
    (category.items || []).reduce((total, item) => total + quantityOf(item.id), 0);

  const renderItemRow = (item: PublicCatalogItemData) => {
    const itemName = item.name[language] || item.name.en || item.name.he || '';
    const itemDescription = item.description?.[language];
    const itemQuantity = quantityOf(item.id);
    const isSelected = itemQuantity > 0;
    const canToggleOnRowClick = !isSingle && (itemQuantity > 0 || selectedQuantity < requiredQuantity);
    const handleRowClick = () => {
      if (isSingle) {
        toggleSingleSelection(item.id);
        return;
      }
      if (itemQuantity > 0) {
        setItemQuantity(item.id, 0);
        return;
      }
      if (selectedQuantity < requiredQuantity) {
        setItemQuantity(item.id, 1);
      }
    };
    return (
      <Grid
        key={item.id}
        container
        flexWrap="nowrap"
        gap={1.5}
        onClick={handleRowClick}
        sx={{
          width: '100%',
          border: `2px solid ${isSelected ? theme.customTheme.palette.primary : theme.customTheme.palette.border}`,
          borderRadius: '8px',
          padding: 1,
          cursor: isSingle || canToggleOnRowClick ? 'pointer' : 'default',
          backgroundColor: isSelected
            ? Color(theme.customTheme.palette.primary).alpha(0.08).string()
            : theme.customTheme.palette.background,
        }}>
        {(item.photo_url || !isSingle) && (
          <Grid item container flexDirection="column" alignItems="center" gap={1.5} width="auto" flexShrink={0}>
            {item.photo_url && (
              <ButtonBase
                aria-label={itemName}
                onClick={(event) => {
                  event.stopPropagation();
                  setEnlargedItem(item);
                }}
                sx={{ borderRadius: '6px', cursor: 'zoom-in' }}>
                <img
                  src={item.photo_url}
                  alt={itemName}
                  style={{ width: '96px', height: '96px', objectFit: 'cover', borderRadius: '6px' }}
                />
              </ButtonBase>
            )}
            {!isSingle && (
              <Grid item onClick={(event) => event.stopPropagation()} sx={{ cursor: 'default' }}>
                <NumberSelector
                  value={itemQuantity}
                  setValue={(value) => setItemQuantity(item.id, value)}
                  min={0}
                  isAddButtonDisabled={selectedQuantity >= requiredQuantity}
                />
              </Grid>
            )}
          </Grid>
        )}
        <Grid container flexDirection="column" flexGrow={1} minWidth={0} gap={0.5}>
          <OrderPageTypography bold>{itemName}</OrderPageTypography>
          {itemDescription && <OrderPageTypography variant="body2">{itemDescription}</OrderPageTypography>}
          <OrderPageTypography bold>{priceFormatterFromCurrency(item.price, currency)}</OrderPageTypography>
        </Grid>
        {isSingle && isSelected && (
          <Grid item flexShrink={0} alignSelf="center">
            <CheckCircle style={{ color: theme.customTheme.palette.primary }} />
          </Grid>
        )}
      </Grid>
    );
  };

  const renderCategory = (category: PublicCatalogCategoryData) => {
    const categoryItems = [...(category.items || [])].sort(
      (firstItem, secondItem) => Number(quantityOf(secondItem.id) > 0) - Number(quantityOf(firstItem.id) > 0),
    );
    const categoryLabel = category.label[language] || category.label.en || category.label.he;
    const categorySelectedQuantity = selectedQuantityInCategory(category);
    const isExpanded = !isCollapsible || expandedCategoryIds.has(category.id);
    const showHeader = isCollapsible || categories.length > 1;
    return (
      <Grid key={category.id} container flexDirection="column" gap={1}>
        {showHeader && (
          <Grid
            container
            alignItems="center"
            gap={1}
            flexWrap="nowrap"
            onClick={isCollapsible ? () => toggleCategory(category.id) : undefined}
            sx={{ cursor: isCollapsible ? 'pointer' : 'default' }}>
            <OrderPageTypography variant="h3" bold>
              {`${categoryLabel} (${categoryItems.length})`}
            </OrderPageTypography>
            {categorySelectedQuantity > 0 && (
              <OrderPageTypography variant="body2">
                {t('project_selection.category_selected', { selected: categorySelectedQuantity })}
              </OrderPageTypography>
            )}
            {isCollapsible &&
              (isExpanded ? (
                <ExpandLess style={{ color: theme.customTheme.palette.text.primary }} />
              ) : (
                <ExpandMore style={{ color: theme.customTheme.palette.text.primary }} />
              ))}
          </Grid>
        )}
        <Collapse in={isExpanded}>
          <Grid container flexDirection="column" gap={1}>
            {categoryItems.map(renderItemRow)}
          </Grid>
        </Collapse>
      </Grid>
    );
  };

  return (
    <Grid container flexDirection="column" gap={2}>
      {!isSingle && (
        <OrderPageTypography variant="h6">
          {t('project_selection.progress', { selected: selectedQuantity, required: requiredQuantity })}
        </OrderPageTypography>
      )}
      {categories.map(renderCategory)}
      <Dialog open={!!enlargedItem} onClose={() => setEnlargedItem(null)} maxWidth="md">
        {enlargedItem?.photo_url && (
          <ButtonBase onClick={() => setEnlargedItem(null)} sx={{ cursor: 'zoom-out' }}>
            <img
              src={enlargedItem.photo_url}
              alt={enlargedItem.name[language] || enlargedItem.name.en || enlargedItem.name.he || ''}
              style={{ display: 'block', maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
            />
          </ButtonBase>
        )}
      </Dialog>
    </Grid>
  );
};
